import { Button, Stack } from 'react-bootstrap';
import { useEffect, useRef, useState } from 'react';

import Common from '../../../stores/Common';
import { IValidation } from '../../../interfaces/IError';
import classNames from 'classnames';
import styles from './FileUploader.module.scss';

const MAX_FILE_SIZE_IN_BYTES = 8_000_000;

export interface IFileUploaderProps {
    disabled: boolean;
    multiple?: boolean;
    onFilesSelected: (file: FileList) => void;
}

export function FileUploader(props: IFileUploaderProps) {
    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounter = useRef<number>(0);
    const [files, setFiles] = useState<FileList | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [validation, setValidation] = useState<IValidation>({});

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragCounter.current += 1;
        setIsDragOver(true);
        setValidation({});
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragCounter.current -= 1;

        if (dragCounter.current === 0) {
            setIsDragOver(false);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (props.disabled) return;

        e.preventDefault();
        setValidation({});
        dragCounter.current = 0;
        setIsDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
        setValidation({});
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.disabled) return;

        if (e.target.files) {
            handleFiles(e.target.files);
        }
    };

    const handleFiles = (files: FileList) => {
        if (props.disabled) return;

        if (!props.multiple && files.length > 1) {
            setValidation({ file: ['Only one file may be uploaded at a time'] });
            return;
        }

        setFiles(files);
    };

    const handleClearFileClick = () => {
        setFiles(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (files) {
            props.onFilesSelected(files);
        }
    }, [files]);

    return (
        <>
            <Stack
                className={classNames(
                    styles.uploader,
                    isDragOver ? styles.dragover : '',
                    props.disabled ? styles.disabled : ''
                )}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleClick}
                style={{ background: 'rgba(0, 0, 0, 0.03)' }}
            >
                {!props.multiple && files && files?.length > 0 ? (
                    <SingleFileSelectedContent file={files[0]} />
                ) : (
                    <EmptyFileUploadContents />
                )}
                {validation.count && (
                    <div className={classNames('text-danger', styles.error)}>
                        {validation.count}
                    </div>
                )}
                {validation.size && (
                    <div className={classNames('text-danger', styles.error)}>{validation.size}</div>
                )}
            </Stack>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                multiple={props.multiple}
            />
        </>
    );
}

function EmptyFileUploadContents() {
    return (
        <Stack direction="horizontal" gap={3} className={styles.innerContent}>
            <i className="far fa-file-circle-plus" style={{ fontSize: '24pt' }} />
            <div>
                <div className="text-lg bold">Click or drag files here</div>
                <div className={styles.subtext}>PDF, TXT, XLS - up to 1GB each</div>
            </div>
        </Stack>
    );
}

function SingleFileSelectedContent(props: { file: File }) {
    return (
        <Stack direction="horizontal" gap={3} className={styles.innerContent}>
            <i className="far fa-file" style={{ fontSize: '24pt' }} />
            <div>
                <div className="text-lg bold">{props.file.name}</div>
                <div className={styles.subtext}>
                    <div>{Common.dateTimeFormat(new Date(props.file.lastModified).toString())}</div>
                    <div className="text-gray">{props.file.size} bytes</div>
                </div>
            </div>
        </Stack>
    );
}
